/**
 * 用户认证 API 接口
 * 
 * 包含:
 * - 用户登录/注册
 * - 密码重置
 * - 第三方登录（Google, 微信等）
 * - 用户信息管理
 * 
 * 后端服务地址: https://www.aiiifly.com（本地开发经 Vite 代理 /api）
 */

import { API_BASE_URL } from '@/config/api'
import i18n from '@/i18n'

/**
 * 错误代码映射
 * 包含所有可能的错误代码及其对应的中英文错误消息
 */
// 错误代码映射（来自 login_register.js）
const ERROR_CODE_MAP: Record<number, { zh: string; en: string }> = {
  // 成功
  1: { zh: '操作成功！', en: 'Operation successful!' },
  // 通用失败
  0: { zh: '操作失败！', en: 'Operation failed!' },
  // 参数错误（10001-19999）
  10001: { zh: '参数无效', en: 'Invalid parameter' },
  10002: { zh: '参数为空', en: 'Parameter is empty' },
  10003: { zh: '参数格式错误', en: 'Parameter format error' },
  10004: { zh: '参数缺失', en: 'Missing parameter' },
  // 用户错误（20001-29999）
  20001: { zh: '用户未登录，请先登录', en: 'User not logged in, please log in first' },
  20002: { zh: '密码错误', en: 'Incorrect password' },
  20003: { zh: '账号已被禁用', en: 'Account has been disabled' },
  20004: { zh: '用户不存在', en: 'User does not exist' },
  20005: { zh: '用户已存在', en: 'User already exists' },
  20006: { zh: '验证码错误', en: 'Verification code error' },
  20007: { zh: '用户注册失败', en: 'User registration failed' },
  20008: { zh: '用户名已存在', en: 'Username already exists' },
  // 系统错误（40001-49999）
  40003: { zh: '上传尺寸过大', en: 'Upload size too large' },
  40004: { zh: '上传文件格式不支持', en: 'Unsupported file format' },
  40005: { zh: '访问频率超过限制', en: 'Access frequency limit exceeded' },
  // 数据错误（50001-59999）
  50001: { zh: '数据未找到', en: 'Data not found' },
  50002: { zh: '数据有误', en: 'Data error' },
  50003: { zh: '数据已存在', en: 'Data already exists' },
  50004: { zh: '验证码错误', en: 'Verification code error' },
  50005: { zh: '您已频繁获取验证码，请24小时后再试', en: 'You have requested verification code too frequently, please try again after 24 hours' },
  50006: { zh: '您已频繁获取验证码，请2小时后再试', en: 'You have requested verification code too frequently, please try again after 2 hours' },
  // 权限错误（70001-79999）
  70001: { zh: '此操作需要登陆系统！', en: 'This operation requires system login!' },
  70002: { zh: '权限不足，无权操作！', en: 'Insufficient permissions!' },
  70003: { zh: '登录状态过期！', en: 'Login session expired!' },
  70004: { zh: 'token已过期', en: 'Token has expired' },
  70005: { zh: '访问次数受限制', en: 'Access count limit reached' },
  70006: { zh: '无效token', en: 'Invalid token' },
  70007: { zh: '签名失败', en: 'Signature failed' },
}

// Read the same active locale used by Vue components. Storage/browser values
// are only fallbacks for API calls made before the app is mounted.
export function getCurrentLanguage(): 'zh' | 'en' {
  try {
    const activeLocale = String(i18n.global.locale.value || '').toLowerCase()
    if (activeLocale.startsWith('en')) return 'en'
    if (activeLocale.startsWith('zh')) return 'zh'

    const savedSource = localStorage.getItem('app_language_source')
    const lang = localStorage.getItem('app_language')?.replace('-', '_').toLowerCase()
    if (savedSource === 'user' && lang) {
      return lang.startsWith('en') ? 'en' : 'zh'
    }
    const browserLang = navigator.language?.toLowerCase() || ''
    return browserLang.startsWith('en') ? 'en' : 'zh'
  } catch {
    return 'zh'
  }
}

// 根据错误代码获取错误消息
export function getErrorMessage(code: number, fallback?: string, language?: 'zh' | 'en'): string {
  const lang = language || getCurrentLanguage()
  const mapping = ERROR_CODE_MAP[code]
  if (mapping) {
    return mapping[lang]
  }
  return fallback || (lang === 'en' ? 'Operation failed' : '操作失败')
}

// API 响应类型
export interface ApiResponse<T = unknown> {
  code: number
  message: string
  data: T
}

/** Normalize backend status messages to the active UI language. */
export function localizeApiResponse<T>(payload: ApiResponse<T>): ApiResponse<T> {
  const code = Number(payload?.code)
  return {
    ...payload,
    code,
    message: getErrorMessage(code),
  }
}

// 用户信息类型
export interface UserInfo {
  uuid: string
  username: string
  email: string | null
  phone: string | null
  avatar: string | null
  authType: 'normal' | 'google' | 'apple' | 'wechat'
  google?: string | null
  apple?: string | null
  wechat?: string | null
}

// 通用 API 请求封装
async function apiRequest<T>(url: string, options: RequestInit): Promise<ApiResponse<T>> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json() as ApiResponse<T>
  return localizeApiResponse(data)
}

// ==================== 认证 API ====================

/**
 * 发送验证码
 * GET /api/auth/get-code?email=xxx 或 ?phone=xxx
 */
export async function sendVerificationCode(contact: string): Promise<ApiResponse<unknown>> {
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact)
  const url = `${API_BASE_URL}/auth/get-code?${isEmail ? 'email' : 'phone'}=${encodeURIComponent(contact)}`

  const response = await fetch(url, { method: 'POST' })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const data = await response.json() as ApiResponse<unknown>
  return localizeApiResponse(data)
}

/**
 * 使用邮箱和密码登录
 * POST /api/user/email_pw/
 */
export async function loginWithEmail(email: string, password: string): Promise<ApiResponse<UserInfo>> {
  return apiRequest(`${API_BASE_URL}/user/email_pw/`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

/**
 * 使用手机号和密码登录
 * POST /api/user/phone_pw/
 */
export async function loginWithPhone(phone: string, password: string): Promise<ApiResponse<UserInfo>> {
  return apiRequest(`${API_BASE_URL}/user/phone_pw/`, {
    method: 'POST',
    body: JSON.stringify({ phone, password }),
  })
}

/**
 * 使用邮箱和验证码登录
 * POST /api/user/email_code/
 */
export async function loginWithEmailCode(email: string, code: string): Promise<ApiResponse<UserInfo>> {
  return apiRequest(`${API_BASE_URL}/user/email_code/`, {
    method: 'POST',
    body: JSON.stringify({ email, user_code: code }),
  })
}

/**
 * 使用手机号和验证码登录
 * POST /api/user/phone_code/
 */
export async function loginWithPhoneCode(phone: string, code: string): Promise<ApiResponse<UserInfo>> {
  return apiRequest(`${API_BASE_URL}/user/phone_code/`, {
    method: 'POST',
    body: JSON.stringify({ phone, user_code: code }),
  })
}

/**
 * 第三方登录（Google/Apple ID/微信）
 * GET /api/auth/login/${type}
 */
export async function getThirdPartyLoginUrl(type: 'google' | 'apple' | 'wechat'): Promise<ApiResponse<string>> {
  const response = await fetch(`${API_BASE_URL}/auth/login/${type}`)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const data = await response.json() as ApiResponse<string>
  return localizeApiResponse(data)
}

/**
 * 获取第三方账号绑定地址
 * GET /api/auth/bind/${type}?uuid=xxx
 */
export async function getThirdPartyBindUrl(type: 'google' | 'apple' | 'wechat', uuid: string): Promise<ApiResponse<string>> {
  const response = await fetch(`${API_BASE_URL}/auth/bind/${type}?uuid=${encodeURIComponent(uuid)}`)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const data = await response.json() as ApiResponse<string>
  return localizeApiResponse(data)
}

/**
 * 处理第三方登录回调
 * 解析 URL 参数：code、message、data
 */
export function handleThirdPartyCallback(): { success: boolean; userInfo?: UserInfo; error?: string } {
  try {
    const urlParams = new URLSearchParams(window.location.search)
  const code = parseInt(urlParams.get('code') || '0')
  const dataStr = urlParams.get('data')

  if (code === 1 && dataStr) {
    try {
      const userInfo: UserInfo = JSON.parse(decodeURIComponent(dataStr))
      return { success: true, userInfo }
    } catch {
      return { success: false, error: getCurrentLanguage() === 'en' ? 'Failed to parse user data' : '用户数据解析失败' }
    }
  }

  return { success: false, error: getErrorMessage(code) }
  } catch {
    return { success: false, error: getCurrentLanguage() === 'en' ? 'Failed to parse callback URL' : '回调地址解析失败' }
  }
}

// ==================== 用户中心 API ====================

/**
 * 更新用户名
 * POST /api/userinfo/update_username/
 */
export async function updateUsername(uuid: string, username: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/update_username/`, {
    method: 'POST',
    body: JSON.stringify({ uuid, username }),
  })
}

/**
 * 更新头像
 * POST /api/userinfo/update_avatar/
 * FormData：uuid、image（blob）
 */
export async function updateAvatar(uuid: string, imageBlob: Blob): Promise<ApiResponse<string>> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('image', imageBlob, 'avatar.png')

  console.log('[updateAvatar] 请求参数:', { uuid, imageBlobSize: imageBlob.size, imageBlobType: imageBlob.type })

  const response = await fetch(`${API_BASE_URL}/userinfo/update_avatar/`, {
    method: 'POST',
    body: formData,
  })

  console.log('[updateAvatar] HTTP状态:', response.status, response.statusText)

  const text = await response.text()
  console.log('[updateAvatar] 响应内容:', text.substring(0, 500))

  // 尝试解析 JSON 响应
  try {
    const data = JSON.parse(text)
    console.log('[updateAvatar] 解析后的数据:', data)
    return localizeApiResponse(data as ApiResponse<string>)
  } catch (error) {
    console.error('[updateAvatar] JSON解析失败，后端返回的不是JSON:', error)
    // 后端返回 HTML 错误页面，说明服务器真的出错了
    if (text.includes('<html>') || text.includes('<!DOCTYPE')) {
      throw new Error(`Server error: ${response.status} - 后端服务器错误，请联系后端检查日志`)
    }
    throw new Error(`HTTP error! status: ${response.status}`)
  }
}

/**
 * 更新密码
 * POST /api/userinfo/updatePw
 */
export async function updatePassword(params: {
  uuid: string
  password: string
  user_code: string
  phone?: string
  email?: string
}): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/updatePw`, {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * 绑定检查：验证手机号/邮箱是否可以绑定
 * POST /api/userinfo/bind_check_phone 或 bind_check_email
 */
export async function checkBindPhone(uuid: string, phone: string, code?: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/bind_check_phone`, {
    method: 'POST',
    body: JSON.stringify({ uuid, phone, ...(code ? { user_code: code } : {}) }),
  })
}

export async function checkBindEmail(uuid: string, email: string, code?: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/bind_check_email`, {
    method: 'POST',
    body: JSON.stringify({ uuid, email, ...(code ? { user_code: code } : {}) }),
  })
}

/**
 * 绑定手机号
 * POST /api/userinfo/bind_phone
 */
export async function bindPhone(uuid: string, phone: string, code: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/bind_phone`, {
    method: 'POST',
    body: JSON.stringify({ uuid, phone, user_code: code }),
  })
}

/**
 * 绑定邮箱
 * POST /api/userinfo/bind_email
 */
export async function bindEmail(uuid: string, email: string, code: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/userinfo/bind_email`, {
    method: 'POST',
    body: JSON.stringify({ uuid, email, user_code: code }),
  })
}

/**
 * 获取绑定手机号所需的验证码
 * POST /api/user/phone_code
 */
export async function sendPhoneBindCode(phone: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/user/phone_code`, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  })
}

/**
 * 获取绑定邮箱所需的验证码
 * POST /api/user/email_code
 */
export async function sendEmailBindCode(email: string): Promise<ApiResponse> {
  return apiRequest(`${API_BASE_URL}/user/email_code`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

/**
 * 删除用户账号
 * POST /api/userinfo/delete_user
 */
export async function deleteUser(uuid: string): Promise<ApiResponse> {
  // 对齐旧项目：delete_user 使用 FormData 提交 uuid
  const formData = new FormData()
  formData.append('uuid', uuid)
  const response = await fetch(`${API_BASE_URL}/userinfo/delete_user`, {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const data = await response.json() as ApiResponse
  return localizeApiResponse(data)
}

// ==================== 工具函数 ====================

/**
 * 验证手机号格式
 */
export function validatePhone(phone: string): boolean {
  return /^1[3-9]\d{9}$/.test(phone)
}

/**
 * 验证邮箱格式
 */
export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * 验证密码格式（至少 6 个可打印 ASCII 字符，不允许空格）
 */
export function validatePassword(password: string): boolean {
  return /^[\x21-\x7E]{6,}$/.test(password)
}

/**
 * 获取账号类型
 */
export function getAccountType(account: string): 'phone' | 'email' | null {
  if (validatePhone(account)) return 'phone'
  if (validateEmail(account)) return 'email'
  return null
}

/**
 * 脱敏手机号
 */
export function maskPhone(phone: string): string {
  return phone.slice(0, 3) + '****' + phone.slice(-4)
}

/**
 * 脱敏邮箱
 */
export function maskEmail(email: string): string {
  const [name, domain] = email.split('@')
  return name.slice(0, 2) + '***@' + domain
}
